import { yupResolver } from '@hookform/resolvers/yup';
import Delete from '@mui/icons-material/Delete';
import ReceiptLong from '@mui/icons-material/ReceiptLong';
import SaveRounded from '@mui/icons-material/SaveRounded';
import Verified from '@mui/icons-material/Verified';
import { Button } from '@src/common/components/button/Button';
import { DatePicker } from '@src/common/components/dates/DatePicker';
import { LoadingSpinner } from '@src/common/components/LoadingSpinner';
import { PageLayout } from '@src/common/components/Page';
import { Stack } from '@src/common/components/Stack';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import { BillToDropdown } from '@src/orders/components/billTo/BillToDropdown';
import { OrderStatusBadge } from '@src/orders/components/OrderStatusBadge';
import { OrderStatus } from '@src/orders/domain/Order';
import type { ReviewableOrder } from '@src/orders/domain/ReviewableOrder';
import { useCurrentOrderMode } from '@src/orders/hooks/useCurrentOrderMode';
import { useGetOrderTransactions } from '@src/orders/hooks/useGetOrderTransactions';
import { useReviewOrder } from '@src/orders/hooks/useReviewOrder';
import { AdditionalFees } from '@src/orders/review/AdditionalFees';
import { BillingSection } from '@src/orders/review/BillingSection';
import { useUpdateBillingCurrency } from '@src/orders/review/hooks/useUpdateBillingCurrency';
import { useUpdateBillTo } from '@src/orders/review/hooks/useUpdateBillTo';
import { useUpdateFees } from '@src/orders/review/hooks/useUpdateFees';
import { OrderPermitsFees } from '@src/orders/review/OrderPermitsFees';
import { OrderTransactions } from '@src/orders/review/OrderTransactions';
import { QuickbookIdBanner } from '@src/orders/review/QuickbookIdBanner';
import { ReviewableOrderDetails } from '@src/orders/review/ReviewableOrderDetails';
import {
  type FeesFormFields,
  PermitInvoiceNumberField,
  PermitServiceFeeField,
  ReviewOrderFieldsDefaultValues,
  ReviewOrderFieldsYupValidation,
} from '@src/orders/schemas/ReviewOrderFormPermitFields';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export const OrderReview = ({ order }: { order: ReviewableOrder }) => {
  const [billingDateValue, setBillingDateValue] = useState(order.billingDate);
  const { t } = useTranslation();
  const defaultValues = ReviewOrderFieldsDefaultValues(order);
  const { updateBillingInformation, canUpdateBillTo } = useUpdateBillTo(order);
  const { updateBillingCurrency } = useUpdateBillingCurrency({ order });
  const { updateFees } = useUpdateFees({ order });
  const { mode } = useCurrentOrderMode();

  const isReview = order.status === OrderStatus.TO_REVIEW;
  const isCancelled = order.status === OrderStatus.CANCELLED;
  const showBillingDate = !isReview && !isCancelled;

  const {
    handleSubmit,
    getValues,
    register,
    control,
    watch,
    setValue,
    trigger,
  } = useForm<FeesFormFields>({
    defaultValues,
    resolver: yupResolver(ReviewOrderFieldsYupValidation(order)),
  });
  const onFeesChanged = async () => {
    if (await trigger()) {
      updateFees({ fees: getValues() });
    }
  };

  const setAllInvoiceNumber = async (
    invoiceNumber: string,
    order: ReviewableOrder,
  ) => {
    order.permitApplicationGroups.forEach((permit) => {
      setValue(
        `permitFees.${permit.originalPermit.id}.${PermitInvoiceNumberField.fieldName}`,
        invoiceNumber,
      );
      permit.subsequentPermits.forEach((subsequentPermit) =>
        setValue(
          `permitFees.${subsequentPermit.id}.${PermitInvoiceNumberField.fieldName}`,
          invoiceNumber,
        ),
      );
    });
  };

  const setAllServiceFee = (amount: string, order: ReviewableOrder) => {
    order.permitApplicationGroups.forEach((group) => {
      const originalId = group.originalPermit.id;
      const originalIsWaived = getValues(
        `permitFees.${originalId}.${PermitServiceFeeField.fieldName}.isWaived`,
      );
      if (!originalIsWaived) {
        setValue(
          `permitFees.${originalId}.${PermitServiceFeeField.fieldName}.amount`,
          amount,
        );
      }
      group.subsequentPermits.forEach((subsequentPermit) => {
        const subIsWaived = getValues(
          `permitFees.${subsequentPermit.id}.${PermitServiceFeeField.fieldName}.isWaived`,
        );
        if (!subIsWaived) {
          setValue(
            `permitFees.${subsequentPermit.id}.${PermitServiceFeeField.fieldName}.amount`,
            amount,
          );
        }
      });
    });
  };
  const {
    reviewOrder,
    completeOrder,
    modifyInvoice,
    updateInvoiceManually,
    deleteInvoice,
    generateInvoice,
    sendInvoiceByEmail,
    invoiceActionStatus,
    isCompletingOrder,
  } = useReviewOrder({
    orderId: order.id,
  });
  const { billingCurrency } = order;
  const { transactions } = useGetOrderTransactions({ orderId: order.id });

  const billingData =
    order.billing && order.billTo.paymentMethod
      ? {
          currency: billingCurrency,
          paymentMethod: order.billTo.paymentMethod,
          totals: order.billing,
        }
      : undefined;

  const missingPayments = billingData && billingData.totals.hasDuePayment();
  const maxBillable =
    billingData?.totals.calculateMaxBillable({ currency: billingCurrency }) ??
    0;

  const readonly = mode === 'viewInvoice' || order.isInvoiced();
  const showBanner = !order.hasNeededQuickbooksIds();
  return (
    <PageLayout
      title={t('orderDetails')}
      subtitle={`#${order.displayId}`}
      style={{ margin: '24px', flex: 1 }}
    >
      {showBanner && <QuickbookIdBanner />}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack flex={1}>
          <OrderStatusBadge status={order.status} />
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center" flex={1}>
          {showBillingDate && (
            <DatePicker
              disabled={!canUpdateBillTo}
              value={
                billingDateValue
                  ? new Date(billingDateValue + 'T00:00:00')
                  : new Date()
              }
              onChange={(date: Date | null) => {
                if (!date) return;
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                setBillingDateValue(`${year}-${month}-${day}`);
              }}
              placeholder={t('billingDate')}
              label={t('billingDate')}
            />
          )}
          <Stack flex={1}>
            <BillToDropdown
              clientId={order.billTo.id}
              disabled={!canUpdateBillTo}
              size="small"
              onChange={(client) => {
                updateBillingInformation({ billTo: client.id });
              }}
            />
          </Stack>
        </Stack>
      </Stack>

      <ReviewableOrderDetails order={order} />

      <form
        onSubmit={handleSubmit(() => {
          if (order.isModifyingInvoice()) {
            modifyInvoice();
            return;
          }
          if (order.isInvoiced()) {
            sendInvoiceByEmail({
              orderId: order.id,
              formValues: getValues(),
            });
            return;
          }
          if (order.canGenerateInvoice()) {
            generateInvoice(billingDateValue);
            return;
          }
          if (billingCurrency) {
            completeOrder({
              orderId: order.id,
              formValues: getValues(),
              billTo: order.billTo.id,
              billingCurrency,
            });
          }
        })}
        className={formStyle}
      >
        <OrderPermitsFees
          order={order}
          totals={order.getPermitFeesTotals()}
          readOnly={readonly}
          register={register}
          control={control}
          watch={watch}
          onFeesChanged={onFeesChanged}
          setAllInvoiceNumber={setAllInvoiceNumber}
          setAllServiceFee={setAllServiceFee}
        />

        <AdditionalFees
          control={control}
          getValues={getValues}
          readonly={readonly}
          onFeesChanged={onFeesChanged}
        />

        <BillingSection
          data={billingData}
          order={order}
          onCurrencyChange={updateBillingCurrency}
          transactions={transactions}
          readonly={readonly}
        />

        {order.billTo.paymentMethod === 'CREDIT_CARD' && (
          <OrderTransactions
            order={order}
            maxBillable={maxBillable}
            disablePayment={!missingPayments}
          />
        )}

        {mode != 'viewInvoice' && (
          <Stack
            direction="row"
            justifyContent="flex-end"
            marginTop="auto"
            paddingTop={theme.spacing['12']}
          >
            {order.canGenerateInvoice() ? (
              <Button
                type="submit"
                variant="contained"
                startIcon={
                  invoiceActionStatus.isGeneratingInvoice ? (
                    <LoadingSpinner sizeInPx={20} />
                  ) : (
                    <ReceiptLong />
                  )
                }
                disabled={
                  !order.canContinue() || invoiceActionStatus.isDisabled
                }
              >
                {t('generateInvoice')}
              </Button>
            ) : (
              order.isInvoicing() && (
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<Verified />}
                  disabled={!order.canContinue()}
                >
                  {t('completeOrder')}
                </Button>
              )
            )}

            {order.isModifyingInvoice() && (
              <Button
                type="submit"
                variant="contained"
                startIcon={
                  invoiceActionStatus.isGeneratingInvoice ? (
                    <LoadingSpinner sizeInPx={20} />
                  ) : (
                    <Verified />
                  )
                }
                disabled={
                  !order.canContinue() || invoiceActionStatus.isDisabled
                }
              >
                {t('updateInvoice')}
              </Button>
            )}

            {order.isInvoiced() && (
              <Stack direction="row" spacing={1}>
                <Button
                  type="button"
                  variant="outlined"
                  startIcon={
                    invoiceActionStatus.isDeletingInvoice ? (
                      <LoadingSpinner sizeInPx={20} />
                    ) : (
                      <Delete />
                    )
                  }
                  disabled={
                    !order.canContinue() || invoiceActionStatus.isDisabled
                  }
                  onClick={deleteInvoice}
                >
                  {t('deleteInvoice')}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={
                    isCompletingOrder ? (
                      <LoadingSpinner sizeInPx={20} />
                    ) : (
                      <Verified />
                    )
                  }
                  disabled={
                    !order.canContinue() ||
                    isCompletingOrder ||
                    invoiceActionStatus.isDisabled
                  }
                >
                  {t('completeOrder')}
                </Button>
              </Stack>
            )}

            {order.isCompleted() && (
              <Button
                type="button"
                variant="contained"
                startIcon={<Verified />}
                onClick={updateInvoiceManually}
              >
                {t('saveChanges')}
              </Button>
            )}

            {order.isReviewing() && (
              <Button
                type="button"
                variant="contained"
                onClick={() =>
                  reviewOrder({ orderId: order.id, formValues: getValues() })
                }
                startIcon={<SaveRounded />}
              >
                {t('reviewOrder')}
              </Button>
            )}
          </Stack>
        )}
      </form>
    </PageLayout>
  );
};

const formStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing['4'],
});
